import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";
import axios from "axios";
import { toast } from "sonner";
import { 
  Award, 
  Zap, 
  Target,
  TrendingUp,
  Building2,
  Mail,
  Calendar,
  Lock,
  Leaf,
  BarChart3,
  Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_ICONS = {
  pioneer: Leaf,
  analyst: BarChart3,
  strategist: Target,
  impact_leader: Award,
  csrd_expert: Shield
};

export default function Profile() {
  const { user, token } = useAuth();
  const [loyaltyProfile, setLoyaltyProfile] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loyaltyRes, badgesRes] = await Promise.all([
        axios.get(`${API}/loyalty/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/loyalty/badges`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLoyaltyProfile(loyaltyRes.data);
      setAllBadges(badgesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du profil");
    } finally {
      setLoading(false);
    }
  };

  const earnedBadgeIds = loyaltyProfile?.badges?.map(b => b.id) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="profile-page">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Mon profil
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez vos informations et suivez votre progression
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* User Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Informations personnelles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                    <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-2xl font-bold text-primary-foreground">
                      {user?.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{user?.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{user?.role === "admin" ? "Administrateur" : "Utilisateur"}</p>
                    </div>
                  </div>
                  
                  <div className="grid gap-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Email</div>
                        <div className="text-sm font-medium">{user?.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Organisation</div>
                        <div className="text-sm font-medium">{user?.organization}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg border">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Membre depuis</div>
                        <div className="text-sm font-medium">
                          {new Date(user?.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Badges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Award className="w-5 h-5 text-chart-4" />
                    Badges RSE
                  </CardTitle>
                  <CardDescription>
                    Débloquez des badges en prenant des décisions durables
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {allBadges.map((badge) => {
                      const isEarned = earnedBadgeIds.includes(badge.id);
                      const IconComponent = BADGE_ICONS[badge.id] || Award;
                      
                      return (
                        <div
                          key={badge.id}
                          className={`badge-item ${isEarned ? 'earned' : 'locked'} border rounded-xl`}
                          data-testid={`badge-${badge.id}`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isEarned ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-foreground">{badge.name}</h4>
                              {isEarned && (
                                <Badge variant="secondary" className="text-xs">Obtenu</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{badge.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Critère : {badge.criteria} • {badge.points_required} pts requis
                            </p>
                          </div>
                          {isEarned ? (
                            <div className="text-chart-1">
                              <Award className="w-6 h-6" />
                            </div>
                          ) : (
                            <div className="text-muted-foreground">
                              <Lock className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Loyalty Stats */}
            <div className="space-y-6">
              {/* Level Card */}
              <Card className="border-2" style={{ borderColor: 'hsl(var(--chart-3))' }}>
                <CardContent className="p-6 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Niveau actuel</div>
                  <div className="text-5xl font-bold text-chart-3" style={{ fontFamily: 'Manrope' }}>
                    {loyaltyProfile?.level || 1}
                  </div>
                  <div className="text-lg font-medium text-foreground mt-1">
                    {loyaltyProfile?.level_name || "Débutant"}
                  </div>
                </CardContent>
              </Card>

              {/* Points */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-chart-4/10 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-chart-4" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Points de fidélité</div>
                      <div className="text-2xl font-bold text-foreground" data-testid="profile-points">
                        {loyaltyProfile?.points || 0}
                      </div>
                    </div>
                  </div>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Prochain niveau</span>
                      <span className="font-medium">{loyaltyProfile?.points_to_next_level || 0} pts</span>
                    </div>
                    <Progress value={loyaltyProfile?.progress_to_next_level || 0} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Statistiques</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      <span className="text-sm">Décisions</span>
                    </div>
                    <span className="font-bold">{loyaltyProfile?.decision_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-chart-4" />
                      <span className="text-sm">Badges obtenus</span>
                    </div>
                    <span className="font-bold">{loyaltyProfile?.badges?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-chart-1" />
                      <span className="text-sm">Progression</span>
                    </div>
                    <span className="font-bold">{Math.round(loyaltyProfile?.progress_to_next_level || 0)}%</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
